import React, { useEffect, useState } from "react";

import { getLocationIcon, getLocationNameLS, RenderFooter, RenderHeader } from "./FbsLanding";
import { applyOrgTheme, getUrlThemeLogoAsset, FBS_TEMPLATE_MISSING_MESSAGE } from "./fbs-common";

import { default as QRCode } from 'qrcode.react';
import { axiosInstance as axios } from "../utils/axios";
import { getStorage, setStorage } from "../utils/storage";
import animConfetti from "../assets/confetti-subtle_lf20_lmggs7gq.json";
import Lottie from "lottie-react";
import { generateId } from "../utils/helpers";
import { PROFILE_IMG_FALLBACK, URL_BASE_FBS } from "../config.common";
import FbsStaffSwitcher, { isStaffSwitchEnabled } from "./FbsStaffSwitcher";
import { CachedImg, rememberImages } from "../utils/assetCache";

export function FbsLandingQR(props) {
    // Chooses a random polling interval (10-15 min) so tabs don't all hit the server together.
    // Especially after some synced reboot etc.
    const generateRandomTime = () => Math.floor(Math.random() * 6) + 10;

    const [configurable, setConfigurable] = useState({
        fetchTemplateEvery: generateRandomTime(), // in minutes, randomized at app start
        showStaffPhoto: true,
        showLHSAnimation: true,
        fallbackImage: PROFILE_IMG_FALLBACK,
        greeting: "Namaskara",
        meta1: "This area is cleaned & maintained by me today.",
        meta2: "How am I doing?",
        qrHeading: "Please share your experience",
        qrSubHeading: "Your feedback will help us improve",
        showLocTxtBelowQR: true,
    });

    const [fbsMeta, setFbsMeta] = useState(getStorage("fbsMeta"));
    const [viewMode, setViewMode] = useState("horizontal"); // horizontal/vertical
    const [elementWidth, setElementWidth] = useState(320);
    const [template, setTemplate] = useState(getStorage("template"));
    const [templateMissing, setTemplateMissing] = useState(false);
    const [templateMissingMessage, setTemplateMissingMessage] = useState("");
    const [staffProfileType, setStaffProfileType] = useState("profile"); // profile/card

    /* Staff Switcher */
    const [staffSwitcherOpen, setStaffSwitcherOpen] = useState(false);
    const staffSwitchEnabled = isStaffSwitchEnabled(template, fbsMeta);

    /* QR CODE CONTENT for displaying QR */
    const prepend = URL_BASE_FBS + "/m/feedback?hash="; // URL_BASE would be the base URL of the feedback system (fbs./feedback.)
    const qrCodePayload = {
        loi: fbsMeta?.location?.id, // Location ID
        lon: fbsMeta?.location?.location, // Location Name
        id: Math.ceil(Math.random() * 100), // Random ID
        hashKey: generateId(6),
        geek: "@thinkdj", // Random Hash Key
        oi: fbsMeta?.location?.org_id ?? fbsMeta?.org_id, // Org ID
    };
    const qrCodeText = btoa(
        unescape(encodeURIComponent(JSON.stringify(qrCodePayload))),
    );

    const getTheTemplate = async () => {
        const apiUrl = "/fbs/public/get-the-template"; // unified code
        try {
            let response = await axios.post(apiUrl, {
                location_id: fbsMeta?.location?.id,
                org_id: fbsMeta?.org_id,
            });
            const orgId = parseInt(fbsMeta?.org_id ?? fbsMeta?.location?.org_id, 10);
            const isTargetOrg = orgId === 1083;

            const tmplId = response?.data?.id ?? null;

            // Frontend-only guard (org-scoped): show rescan message when template id isn't resolved.
            if (isTargetOrg && (!tmplId || tmplId === 0)) {
                setTemplateMissing(true);
                setTemplateMissingMessage(FBS_TEMPLATE_MISSING_MESSAGE);
                return;
            }
            if (response?.data?.id === 0) {
                console.error(
                    "No associated templates found for " +
                        fbsMeta?.location?.location +
                        ". Using default",
                );
            } else if (response && response.data) {
                setTemplate(response.data);
            }

            const orgNameString = applyOrgTheme(
                response?.data?.org?.name,
                response?.data?.org?.shortname,
            );
            /* BIAL-specific QR overrides */
            if (orgNameString.includes("bial")) {
                setConfigurable({ ...configurable, showLocTxtBelowQR: false });
                setElementWidth(() => (window.innerWidth > 1600 ? 400 : 300));
            }
        } catch (e) {
            console.error("Failed to fetch QR template:", e);
        }
    };
    /* Init, first call */
    useEffect(() => {
        getTheTemplate().then();
    }, [fbsMeta]);

    /* Get template updates every x minutes */
    const interval = React.useRef();
    useEffect(() => {
        interval.current = setInterval(
            () => {
                getTheTemplate().then();
            },
            configurable.fetchTemplateEvery * 1000 * 60,
        );
        return () => {
            clearInterval(interval.current);
        };
    }, []);

    const logoToUse =
        props.logo ??
        getUrlThemeLogoAsset() ??
        template?.org?.logo_img ??
        getStorage("u4")?.hotel?.logo_img ??
        null;

    useEffect(() => {
        rememberImages([logoToUse].filter(Boolean));
    }, [logoToUse]);

    return (
        <>
            <div id="header">
                <div id="header-left">{/* LHS: Empty*/}</div>
                <div id="header-center"></div>
                <div id="header-right">
                    {/* RHS */}
                    {logoToUse ? <CachedImg src={logoToUse} alt="" /> : <></>}
                </div>
            </div>

            <div style={{ textAlign: "center" }}>
                {templateMissing ? (
                    <div style={{ paddingTop: 80 }}>
                        <h3 style={{ marginTop: 0 }}>
                            {templateMissingMessage ||
                                FBS_TEMPLATE_MISSING_MESSAGE}
                        </h3>
                    </div>
                ) : viewMode === "horizontal" ? (
                    <div
                        className="fbs-qr-container"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2,1fr)",
                            minWidth: 1200,
                            gridGap: 50,
                            alignItems: "center",
                        }}
                    >
                        {/* LHS */}
                        <div
                            style={{
                                paddingTop: 24,
                                paddingRight: 16,
                                borderRight: "2px solid rgba(255,255,255,0.32)",
                                position: "relative",
                            }}
                        >
                            {configurable.showStaffPhoto ? (
                                <div
                                    className={
                                        staffSwitchEnabled
                                            ? "fbs-staff-switchable-indicator"
                                            : ""
                                    }
                                    onClick={() => {
                                        if (staffSwitchEnabled)
                                            setStaffSwitcherOpen(true);
                                    }}
                                    style={
                                        staffSwitchEnabled
                                            ? {
                                                  cursor: "pointer",
                                                  display: "inline-block",
                                              }
                                            : { display: "inline-block" }
                                    }
                                >
                                    <img
                                        src={
                                            template?.location?.assoc_staff
                                                ?.avatar ??
                                            configurable.fallbackImage
                                        }
                                        alt="Photo"
                                        className="fbs-loc-staff-profile-img"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src =
                                                configurable.fallbackImage;
                                        }}
                                    />
                                </div>
                            ) : null}
                            <h3
                                style={{
                                    textAlign: "center",
                                    lineHeight: 1.5,
                                    fontSize: "1.5rem",
                                }}
                            >
                                <span>
                                    {configurable.greeting ? (
                                        <strong className="fbs-greeting">
                                            {configurable.greeting}
                                        </strong>
                                    ) : null}
                                    {(template?.location?.assoc_staff?.fname ??
                                    template?.location?.assoc_staff?.name) ? (
                                        <span className="fbs-greeting">
                                            , I'm{" "}
                                            {template?.location?.assoc_staff
                                                ?.fname ??
                                                template?.location?.assoc_staff
                                                    ?.name}
                                        </span>
                                    ) : null}
                                </span>
                                {configurable.meta1 ? (
                                    <small
                                        style={{
                                            opacity: 0.5,
                                            display: "block",
                                            paddingTop: 18,
                                            paddingBottom: 2,
                                        }}
                                    >
                                        {configurable.meta1}
                                    </small>
                                ) : null}
                                {configurable.meta2 ? (
                                    <small
                                        style={{
                                            opacity: 0.5,
                                            display: "block",
                                        }}
                                    >
                                        {configurable.meta2}
                                    </small>
                                ) : null}
                            </h3>
                            {configurable.showLHSAnimation ? (
                                <Lottie
                                    animationData={animConfetti}
                                    loop={true}
                                    className={``}
                                    style={{
                                        width: elementWidth * 1.975,
                                        position: "absolute",
                                        zIndex: 0,
                                        top: -150,
                                        left: -20,
                                    }}
                                />
                            ) : null}
                        </div>

                        {/* RHS */}
                        <div>
                            <h5
                                style={{
                                    padding: 0,
                                    marginTop: 20,
                                    fontSize: "1.5rem",
                                    lineHeight: 1.5,
                                }}
                            >
                                {configurable.qrHeading ? (
                                    <span className="fbs-qr-heading">
                                        {configurable.qrHeading}
                                    </span>
                                ) : null}
                                {configurable.qrSubHeading ? (
                                    <small
                                        style={{
                                            opacity: 0.5,
                                            display: "block",
                                        }}
                                    >
                                        {configurable.qrSubHeading}
                                    </small>
                                ) : null}
                            </h5>

                            <div
                                style={{
                                    padding: 18,
                                    background: "#fff",
                                    marginBottom: 10,
                                    width: elementWidth,
                                    height: elementWidth,
                                    boxSizing: "content-box",
                                    textAlign: "center",
                                    margin: "0 auto",
                                }}
                                className="fbs-block-prominent"
                            >
                                <a href={prepend + qrCodeText} target="_dj">
                                    <QRCode
                                        value={prepend + qrCodeText}
                                        size={elementWidth}
                                        bgColor={"#FFF"}
                                        fgColor={"#000"}
                                        level={"L"}
                                        includeMargin={false}
                                        renderAs={"svg"}
                                    ></QRCode>
                                </a>
                            </div>
                            {configurable.showLocTxtBelowQR ? (
                                <b className="d-b p-t-b-25 col-dark opa-050 f-s-1-2r">
                                    {getLocationIcon()} {getLocationNameLS()}
                                </b>
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <></>
                )}

                {viewMode === "vertical" ? (
                    <div>
                        {staffProfileType === "profile" ? (
                            <div style={{ position: "relative" }}>
                                <img
                                    src="https://randomuser.me/api/portraits/men/47.jpg"
                                    alt=""
                                    style={{
                                        borderRadius: "50%",
                                        width: 200,
                                        objectFit: "cover",
                                        border: "8px solid rgba(0,0,0,.125)",
                                        position: "absolute",
                                        zIndex: 1000,
                                        left: "50%",
                                        marginLeft: "-20%",
                                        top: 65,
                                    }}
                                />
                                <Lottie
                                    animationData={animConfetti}
                                    loop={true}
                                    className={``}
                                    style={{ width: 490 }}
                                />
                                <h3
                                    style={{
                                        textAlign: "center",
                                        lineHeight: 1.3,
                                    }}
                                >
                                    Hi, I'm Rahul
                                    <small
                                        style={{
                                            opacity: 0.6,
                                            display: "block",
                                        }}
                                    >
                                        How am I doing?
                                    </small>
                                </h3>
                            </div>
                        ) : (
                            <></>
                        )}
                        {staffProfileType === "card" ? (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "75px auto",
                                    gridGap: 15,
                                    padding: "10px 5px",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                className="fbs-block-prominent"
                            >
                                <div>
                                    <img
                                        src="https://randomuser.me/api/portraits/women/37.jpg"
                                        alt=""
                                        style={{
                                            borderRadius: "50%",
                                            width: "100%",
                                            objectFit: "cover",
                                            border: "4px solid rgba(0,0,0,.075)",
                                        }}
                                    />
                                </div>
                                <h4
                                    style={{
                                        textAlign: "left",
                                        lineHeight: 1.3,
                                    }}
                                >
                                    Hi, I'm Reshmi
                                    <small
                                        style={{
                                            opacity: 0.6,
                                            display: "block",
                                        }}
                                    >
                                        How am I doing?
                                    </small>
                                </h4>
                            </div>
                        ) : (
                            <></>
                        )}
                        <div>
                            <h5 style={{ padding: 0, marginTop: 20 }}>
                                Please share your feedback
                            </h5>

                            <div
                                style={{
                                    padding: 18,
                                    background: "#fff",
                                    marginBottom: 10,
                                    width: elementWidth,
                                    height: elementWidth,
                                    boxSizing: "content-box",
                                    textAlign: "center",
                                    margin: "0 auto",
                                }}
                                className="fbs-block-prominent"
                            >
                                <QRCode
                                    value={prepend + qrCodeText}
                                    size={elementWidth}
                                    bgColor={"#FFFFFF"}
                                    fgColor={"#000000"}
                                    level={"L"}
                                    includeMargin={false}
                                    renderAs={"svg"}
                                ></QRCode>
                            </div>
                            <b className="d-b p-t-b-10 col-dark opa-050 f-s-1-2r">
                                {getLocationIcon()} {getLocationNameLS()}
                            </b>
                        </div>
                    </div>
                ) : (
                    <></>
                )}
            </div>
            <RenderFooter />

            {/* Staff Switcher Modal */}
            <FbsStaffSwitcher
                isOpen={staffSwitcherOpen}
                onClose={() => setStaffSwitcherOpen(false)}
                orgId={fbsMeta?.org_id ?? fbsMeta?.location?.org_id}
                locationId={fbsMeta?.location?.id}
                currentStaffId={template?.location?.assoc_staff?.id}
                onSwitchSuccess={(locationData) => {
                    if (locationData) {
                        getTheTemplate();
                    }
                }}
            />
        </>
    );
};
